import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../csrf_token"

// Toggles a markdown textarea between its edit pane and a server-rendered
// preview. Preview posts the *unsaved* textarea content to an admin endpoint
// that runs the same render_markdown helper as the public page, so what an
// editor sees matches production exactly (rather than a second, divergent
// client-side markdown renderer).
export default class extends Controller {
  static targets = ["input", "output", "editPane", "previewPane", "editTab", "previewTab"]
  static values = { url: String }

  showEdit() {
    this.previewPaneTarget.setAttribute("hidden", "")
    this.editPaneTarget.removeAttribute("hidden")
    this._select(this.editTabTarget, this.previewTabTarget)
  }

  async showPreview() {
    this._select(this.previewTabTarget, this.editTabTarget)
    this.editPaneTarget.setAttribute("hidden", "")
    this.previewPaneTarget.removeAttribute("hidden")
    this.outputTarget.innerHTML = "<p class=\"text-sm text-neutral-400\">Rendering…</p>"

    const body = new FormData()
    body.append("markdown", this.inputTarget.value)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken() },
        body,
      })
      // Only a direct 200 is the trusted preview fragment. A 422/500, or a
      // redirect to the login page after the session lapsed (fetch follows it
      // transparently, so response.ok is true), returns unrelated HTML — show a
      // message rather than inject a whole page into the preview pane.
      if (!response.ok || response.redirected) {
        this._showError()
        return
      }
      this.outputTarget.innerHTML = await response.text()
    } catch {
      // Network failure — the request never completed.
      this._showError()
    }
  }

  _showError() {
    this.outputTarget.innerHTML =
      "<p class=\"text-sm text-red-800\">Preview failed. Save your work and try again.</p>"
  }

  _select(selected, deselected) {
    selected.setAttribute("aria-selected", "true")
    deselected.setAttribute("aria-selected", "false")
  }
}
